import { Link } from 'react-router'
import { Banner, Button } from '../../design/components'
import { useAuth } from '../../auth/AuthProvider'
import { useAnnouncements } from './useAnnouncements'

/**
 * "A fight has started at The Velvet Room — join."
 *
 * This is how a player is told, and it is in the shell rather than on a screen because the whole
 * point is that it reaches someone who is not looking at anything in particular
 * (`docs/web-api/phase-9-kickoff.md` §1, decision 6).
 *
 * It is an **in-app** announcement, not an operating-system notification. Web Push would reach a
 * phone with the app closed, and it is a stage of its own — VAPID keys, a subscription table,
 * service-worker handlers, and a permission prompt many people refuse. When it lands it becomes a
 * second sender in front of the same event and changes nothing here, which is why this is not
 * throwaway work.
 */
export function LiveAnnouncement({ enabled }: { enabled: boolean }) {
  const { user } = useAuth()
  const { latest, dismiss } = useAnnouncements(enabled)

  if (latest === null) return null

  // Not to the person who started it. They pressed the button a second ago and are looking at
  // the encounter that spawned it; telling them a fight has started is noise. They still get
  // the push itself, which is what refreshes the Live tab.
  if (user !== null && latest.gameMasterId === user.id) return null

  return (
    <Banner
      tone="ember"
      urgent
      actions={
        <>
          <Button variant="secondary" onClick={dismiss}>
            Not now
          </Button>
          <Link to={`/live/${latest.sessionId}`} onClick={dismiss}>
            <Button>Join</Button>
          </Link>
        </>
      }
    >
      A fight has started at {latest.locationName} — {latest.name}.
    </Banner>
  )
}
